import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service } from '@/services/pmt2Service';
import { GpoHistoryEntry } from '@/services/pmt2GpoHistoryService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';

interface GpoHistoryDashboardProps {
  product: PMT2Product | null;
}

// Interface is now imported from pmt2Service
/*interface GpoHistoryEntry {
  date: Date;
  daysAgo: number;
  'Your Name': string;
  'New Value': string;
  note?: string;
  [key: string]: any;
}

/**
 * GPO History Dashboard Component
 * 
 * Displays a table of GPO history entries for a selected product
 * Shows Days Ago, Date, Your Name, New Value, and Note columns
 */
const GpoHistoryDashboard: React.FC<GpoHistoryDashboardProps> = ({ product }) => {
  const [data, setData] = useState<GpoHistoryEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!product) {
        setData([]);
        setError(null);
        return setLoading(false);
      }

      try {
        setLoading(true);
        // We need to add the getGpoHistory method to pmt2Service
        const historyData = await pmt2Service.getGpoHistory(product.companyName, product.id);
        setData(historyData);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err instanceof Error ? err.message : err);
        setError('Failed to load GPO history data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [product]);

  // Format date as MM/DD/YY
  const formatDate = (date: Date): string => {
    return format(date, 'MM/dd/yy');
  };

  // Format currency value
  const formatCurrency = (value: string | null | undefined): string => {
    if (value === undefined || value === null) return '-';
    
    // Remove any non-numeric characters except decimal point
    const numericValue = value.toString().replace(/[^0-9.]/g, '');
    
    // Parse as float and format as currency
    const parsedValue = parseFloat(numericValue);
    if (isNaN(parsedValue)) return value.toString();
    
    return `$${parsedValue.toFixed(2)}`;
  };

  if (!product) return null;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">GPO History</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">GPO History</h2>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">GPO History</h2>
        <p className="text-gray-500 dark:text-gray-400">No GPO history data available for this product.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm max-w-5xl mx-auto"> {/* Further reduced width */}
      <h2 className="text-2xl font-bold mb-6">GPO History</h2>
      
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/6">Days Ago</TableHead>
              <TableHead className="w-1/6">Date</TableHead>
              <TableHead className="w-1/6">Your Name</TableHead>
              <TableHead className="w-1/6">New Value</TableHead>
              <TableHead className="w-2/6">Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((entry, index) => (
              <TableRow key={index} className={`${index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-900'} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150`}> {/* Added hover effect */}
                <TableCell className="py-1 px-2">{entry.daysAgo}</TableCell> {/* Reduced padding */}
                <TableCell className="py-1 px-2">{formatDate(entry.date)}</TableCell> {/* Reduced padding */}
                <TableCell className="py-1 px-2">{entry['Your Name'] || '-'}</TableCell> {/* Reduced padding */}
                <TableCell className="py-1 px-2">{formatCurrency(entry['New Value'])}</TableCell> {/* Reduced padding */}
                <TableCell className="py-1 px-2">{entry.note || '-'}</TableCell> {/* Reduced padding */}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default GpoHistoryDashboard;